import { useEffect, useState } from "react";
import {
  UserRound,
  BriefcaseBusiness,
  Star,
  MapPin,
  ShieldCheck,
  Crown,
  X,
  FileText,
  Award,
  CalendarDays,
  MessageSquareQuote,
} from "lucide-react";

import { getUserById } from "../../services/authService";
import {
  getUserEvaluations,
  getEvaluationSummary,
} from "../../services/evaluationService";

import "./CompanyViewFreelancerModal.scss";

const IconStar = ({ filled }) => (
  <Star
    size={16}
    strokeWidth={1.8}
    fill={filled ? "currentColor" : "none"}
  />
);

export default function CompanyViewFreelancerModal({
  freelancerId,
  onClose,
}) {
  const [profile, setProfile] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [profileData, evaluationsData, summaryData] =
          await Promise.all([
            getUserById(freelancerId),
            getUserEvaluations(freelancerId),
            getEvaluationSummary(freelancerId),
          ]);

        setProfile(profileData);
        setEvaluations(evaluationsData);
        setSummary(summaryData);
      } catch (err) {
        console.error("Erro ao carregar perfil do freelancer:", err);
      } finally {
        setLoading(false);
      }
    };

    if (freelancerId) {
      fetchData();
    }
  }, [freelancerId]);

  const averageRating =
    evaluations.length > 0
      ? evaluations.reduce((sum, evaluation) => sum + evaluation.rating, 0) /
        evaluations.length
      : 0;

  /*
   * Aceita tanto isGold quanto currentPlan.
   * Isso deixa o componente mais resistente caso o backend
   * altere a forma como informa o plano.
   */
  const isGold =
    Boolean(profile?.isGold) ||
    profile?.currentPlan?.toLowerCase()?.includes("gold");

  const displayName = profile?.nomeSocial || profile?.name || "Freelancer";

  const initials = displayName
    .split(" ")
    .slice(0, 2)
    .map((name) => name[0])
    .join("")
    .toUpperCase();

  return (
    <div className="cvf-overlay" onClick={onClose}>
      <div
        className={`cvf-modal ${isGold ? "cvf-modal--gold" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="cvf-close"
          onClick={onClose}
          aria-label="Fechar perfil"
          type="button"
        >
          <X size={20} />
        </button>

        {loading && (
          <div className="cvf-loading">
            <div className="cvf-loading-spinner" />
            <span>Carregando perfil...</span>
          </div>
        )}

        {!loading && profile && (
          <div className="cvf-body">

            {/* ================= HEADER ================= */}

            <header className="cvf-header">
              <div className="cvf-header-decoration cvf-header-decoration--one" />
              <div className="cvf-header-decoration cvf-header-decoration--two" />

              <div className="cvf-profile-top">
                <div
                  className={`cvf-avatar-wrapper ${
                    isGold ? "cvf-avatar-wrapper--gold" : ""
                  }`}
                >
                  {profile.avatarUrl ? (
                    <img
                      src={profile.avatarUrl}
                      alt={displayName}
                      className="cvf-avatar"
                    />
                  ) : (
                    <div className="cvf-avatar-fallback">{initials}</div>
                  )}

                  {isGold && (
                    <span className="cvf-avatar-crown">
                      <Crown size={14} fill="currentColor" />
                    </span>
                  )}
                </div>

                <div className="cvf-header-info">
                  <div className="cvf-name-row">
                    <h2 className="cvf-name">{displayName}</h2>

                    {isGold && (
                      <span className="cvf-plan-badge">
                        <Crown size={14} fill="currentColor" />
                        Gold
                      </span>
                    )}
                  </div>

                  <div className="cvf-profile-label">
                    <UserRound size={14} />
                    Freelancer
                  </div>

                  <div className="cvf-rating">
                    <div className="cvf-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <IconStar
                          key={star}
                          filled={star <= Math.round(averageRating)}
                        />
                      ))}
                    </div>

                    <strong>
                      {evaluations.length > 0
                        ? averageRating.toFixed(1)
                        : "—"}
                    </strong>

                    <span className="cvf-rating-count">
                      {evaluations.length > 0
                        ? `${evaluations.length} avaliação${
                            evaluations.length > 1 ? "s" : ""
                          }`
                        : "Sem avaliações"}
                    </span>
                  </div>
                </div>
              </div>

              {isGold && (
                <div className="cvf-gold-highlight">
                  <div className="cvf-gold-highlight__icon">
                    <Crown size={18} fill="currentColor" />
                  </div>

                  <div>
                    <strong>Freelancer Gold</strong>
                    <span>
                      Perfil com benefícios e destaque premium na plataforma.
                    </span>
                  </div>

                  <ShieldCheck size={19} className="cvf-gold-highlight__check" />
                </div>
              )}

              {summary?.badge && (
                <div className="cvf-achievement">
                  <Award size={16} />
                  <span>{summary.badge}</span>
                </div>
              )}
            </header>

            {/* ================= STATS ================= */}

            <div className="cvf-stats">
              <div className="cvf-stat">
                <div className="cvf-stat__icon">
                  <Star size={17} />
                </div>

                <div>
                  <strong>
                    {evaluations.length > 0
                      ? averageRating.toFixed(1)
                      : "—"}
                  </strong>
                  <span>Avaliação</span>
                </div>
              </div>

              <div className="cvf-stat">
                <div className="cvf-stat__icon">
                  <MessageSquareQuote size={17} />
                </div>

                <div>
                  <strong>{evaluations.length}</strong>
                  <span>Avaliações</span>
                </div>
              </div>

              <div className="cvf-stat">
                <div className="cvf-stat__icon">
                  <BriefcaseBusiness size={17} />
                </div>

                <div>
                  <strong>{profile.workExperiences?.length || 0}</strong>
                  <span>Experiências</span>
                </div>
              </div>
            </div>

            {/* ================= CONTENT ================= */}

            <main className="cvf-content">

              {/* SOBRE */}

              {profile.bio && (
                <section className="cvf-section">
                  <div className="cvf-section-heading">
                    <div className="cvf-section-icon">
                      <FileText size={17} />
                    </div>

                    <div>
                      <h3>Sobre o profissional</h3>
                      <span>Apresentação</span>
                    </div>
                  </div>

                  <div className="cvf-bio">
                    <p>{profile.bio}</p>
                  </div>
                </section>
              )}

              {/* ÁREAS */}

              <section className="cvf-section">
                <div className="cvf-section-heading">
                  <div className="cvf-section-icon">
                    <BriefcaseBusiness size={17} />
                  </div>

                  <div>
                    <h3>Áreas de atuação</h3>
                    <span>Especialidades profissionais</span>
                  </div>
                </div>

                <div className="cvf-tags">
                  {profile.areas && profile.areas.length > 0 ? (
                    profile.areas.map((area, index) => (
                      <span key={index} className="cvf-tag">
                        {area}
                      </span>
                    ))
                  ) : (
                    <p className="cvf-empty">
                      Nenhuma área especificada.
                    </p>
                  )}
                </div>
              </section>

              {/* EXPERIÊNCIAS */}

              <section className="cvf-section">
                <div className="cvf-section-heading">
                  <div className="cvf-section-icon">
                    <BriefcaseBusiness size={17} />
                  </div>

                  <div>
                    <h3>Histórico profissional</h3>
                    <span>Experiências registradas</span>
                  </div>
                </div>

                {profile.workExperiences &&
                profile.workExperiences.length > 0 ? (
                  <div className="cvf-experiences">
                    {profile.workExperiences.map((exp, index) => (
                      <div key={`${exp.companyName}-${exp.position}-${index}`} className="cvf-exp">
                        <div className="cvf-exp__timeline">
                          <div className="cvf-exp__dot" />

                          {index !== profile.workExperiences.length - 1 && (
                            <div className="cvf-exp__line" />
                          )}
                        </div>

                        <div className="cvf-exp__content">
                          <div className="cvf-exp__top">
                            <div>
                              <h4>{exp.position}</h4>
                              <p>{exp.companyName}</p>
                            </div>

                            <span className="cvf-exp__date">
                              <CalendarDays size={13} />
                              {exp.workMonth}/{exp.workYear}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="cvf-empty-box">
                    <BriefcaseBusiness size={22} />
                    <p>Nenhuma experiência registrada.</p>
                  </div>
                )}
              </section>

              {/* AVALIAÇÕES */}

              <section className="cvf-section cvf-section--last">
                <div className="cvf-section-heading">
                  <div className="cvf-section-icon">
                    <Star size={17} />
                  </div>

                  <div>
                    <h3>Avaliações de empresas</h3>
                    <span>Experiências de contratantes anteriores</span>
                  </div>
                </div>

                {evaluations.length > 0 ? (
                  <div className="cvf-evaluations">
                    {evaluations.map((evaluation, index) => (
                      <article
                        key={index}
                        className="cvf-eval-card"
                      >
                        <div className="cvf-eval-header">
                          <div className="cvf-eval-company">
                            <div className="cvf-company-avatar">
                              {evaluation.evaluatorName
                                ?.slice(0, 1)
                                .toUpperCase()}
                            </div>

                            <strong>{evaluation.evaluatorName}</strong>
                          </div>

                          <div className="cvf-eval-stars">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <IconStar
                                key={star}
                                filled={star <= evaluation.rating}
                              />
                            ))}
                          </div>
                        </div>

                        {evaluation.comment && (
                          <div className="cvf-eval-comment">
                            <MessageSquareQuote size={15} />
                            <p>{evaluation.comment}</p>
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="cvf-empty-box">
                    <Star size={24} />
                    <strong>Ainda sem avaliações</strong>
                    <p>
                      Este profissional ainda não possui avaliações de
                      contratantes.
                    </p>
                  </div>
                )}
              </section>
            </main>

            <footer className="cvf-footer">
              <MapPin size={15} />
              <span>Perfil do profissional</span>

              {isGold && (
                <>
                  <span className="cvf-footer-separator">•</span>

                  <Crown size={14} fill="currentColor" />

                  <span>Freelancer Gold</span>
                </>
              )}
            </footer>
          </div>
        )}

        {!loading && !profile && (
          <div className="cvf-error">
            <UserRound size={32} />
            <strong>Não foi possível carregar o perfil</strong>
            <span>Tente novamente em alguns instantes.</span>

            <button type="button" onClick={onClose}>
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}